import DOMElementFactory from 'Editor/services/DOMUtilities/DOMElementFactory/DOMElementFactory';
import { ELEMENTS, DISPLAY_TYPES } from 'Editor/services/consts';

import './FieldElement.scss';
import EditorManager from 'Editor/services/EditorManager';
import { EditorDOMUtils } from 'Editor/services/_Common/DOM';

import { BaseViewElement } from '../../BaseViewBuilder';
export const ATTRIBUTES = [
  'type',
  'reference',
  'format',
  'captionLabel',
  'link',
  'error',
  'tempCrossReferenceTarget',
] as const;
// const ATTRIBUTES_LENGTH = ATTRIBUTES.length;

// type Styles = {
//   type: string;
//   reference: string;
//   format: string;
//   captionLabel: string;
//   link: string;
//   error: string;
//   tempCrossReferenceTarget: string;
// };

export class FieldElement extends BaseViewElement {
  constructor(Visualizer?: Editor.Visualizer.State, Data?: Editor.Data.API) {
    super(Visualizer, Data);

    this.selectField = this.selectField.bind(this);
    this.deselectField = this.deselectField.bind(this);

    this.handleOnTooltipClick = this.handleOnTooltipClick.bind(this);
    this.destroyLinkTooltip = this.destroyLinkTooltip.bind(this);
    this._handleErrorState = this._handleErrorState.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.handleOnTooltipClick);
    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);

    this._handleErrorState();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleOnTooltipClick);
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);

    this.destroyLinkTooltip();
    super.disconnectedCallback();
  }

  get displayType() {
    return DISPLAY_TYPES.INLINE;
  }

  get isEditable() {
    return ELEMENTS.FieldElement.TYPES.EDITABLE.includes(
      // @ts-expect-error
      this.dataset?.type as Editor.Elements.FieldElementTypes,
    );
  }

  get isContentWrapper() {
    return this.isEditable;
  }

  get fieldType() {
    return this.dataset?.type;
  }

  get fieldLabel() {
    return this.dataset?.cpt;
  }

  get fieldReference() {
    return this.dataset?.ref;
  }

  get format() {
    return this.dataset?.format;
  }

  get selected() {
    return !!this.getAttribute('selected');
  }

  set selected(value) {
    if (value === true) {
      this.setAttribute('selected', 'true');
    } else {
      this.removeAttribute('selected');
    }
  }

  selectField() {
    this.setAttribute('selected', 'true');
  }

  deselectField() {
    this.removeAttribute('selected');
  }

  isTypeCaption() {
    return this.fieldType === ELEMENTS.FieldElement.TYPES.CAPTION;
  }

  isFieldLink() {
    return this.dataset?.link === 'true';
  }

  isBroken() {
    return this.dataset?.error === 'true';
  }

  onMouseEnter() {
    const closestHyperlink = EditorDOMUtils.closest(this, ELEMENTS.HyperlinkElement.TAG);

    if (
      !this.isBroken() &&
      this.isFieldLink() &&
      this.dataset?.type === 'cr' &&
      !closestHyperlink
    ) {
      const widgetsManager = this.Visualizer?.getWidgetsManager?.();
      if (widgetsManager) {
        widgetsManager.addWidget('tooltip', this, {
          content: 'GO_TO_ELEMENT',
          testId: this.id,
        });
      }
    }
  }

  onMouseLeave() {
    if (!this.isBroken() && this.isFieldLink() && this.dataset?.type === 'cr') {
      const widgetsManager = this.Visualizer?.getWidgetsManager?.();
      if (widgetsManager) {
        widgetsManager.removeWidget('tooltip', this.id);
      }
    }
  }

  updateAttributes(options: Editor.Data.CrossReferences.PresentationTextOptionsType) {
    if (this.dataset) {
      if (options.format != null) {
        let value = options.format;
        if (value) {
          if (typeof options.format === 'object') {
            value = options.format;
          }
          this.dataset.format = value;
        }
      }

      if (options.link === true) {
        this.dataset.link = `${options.link}`;
      } else {
        delete this.dataset.link;
      }

      if (options.target != null) {
        this.dataset.ref = options.target;
      }

      if (
        options.label != null &&
        (this.dataset.type === ELEMENTS.FieldElement.TYPES.CAPTION ||
          this.dataset.type === ELEMENTS.FieldElement.TYPES.STYLE_REF)
      ) {
        this.dataset.cpt = options.label;
      }
    }
  }

  setContent(content: string | HTMLElement | any[]) {
    const childElements = [];
    if (content == null) {
      if (this.dataset) {
        this.dataset.broken = 'true';
      }
      const formatElementStyles: { [index: string]: string } = {};
      formatElementStyles.highlightcolor = 'FFF7CE';
      formatElementStyles.color = '2E2E4C';

      // TODO: call view factory instead
      const formatElement = DOMElementFactory.buildElement('format-element', formatElementStyles);
      if (formatElement instanceof BaseViewElement) {
        formatElement.preRender();
      }
      EditorDOMUtils.appendNode(this, formatElement);
      formatElement.appendChild(
        DOMElementFactory.createTextNode(' Broken cross-reference, reference source not found '),
      );
      childElements.push(formatElement);
    } else if (Array.isArray(content)) {
      // is array
    } else if (typeof content === 'string') {
      // is string
      childElements.push(DOMElementFactory.createTextNode(content));
    } else if (content.nodeType != null) {
      // is node
      childElements.push(content);
    }

    // remove childs
    for (let i = 0; i < this.childNodes.length; i++) {
      this.removeChild(this.childNodes[i]);
    }

    // append new content
    for (let j = 0; j < childElements.length; j++) {
      this.appendChild(childElements[j]);
    }
  }

  private handleOnTooltipClick() {
    if (!this.isBroken() && this.isFieldLink() && this.dataset?.type === 'cr') {
      EditorManager.getInstance().handleCrossReferenceSelected(this.dataset.ref);
    }
  }

  private destroyLinkTooltip() {
    if (!this.isBroken() && this.isFieldLink() && this.dataset?.type === 'cr') {
      const widgetsManager = this.Visualizer?.getWidgetsManager?.();
      if (widgetsManager) {
        widgetsManager.removeWidget('tooltip', this.id);
      }
    }
  }

  _handleErrorState() {
    if (this.dataset?.error) {
      this.style.fontWeight = 'bold';
      // this.style.background = '#FFFBE6';
      // this.style.border = '1px solid #FEE66B';
      // this.style.borderRadius = '0.5rem';
      // this.style.padding = '0.5rem 1rem';
      // this.style.whiteSpace = 'nowrap';
      // if (!this.errorIcon) {
      //   this.errorIcon = document.createElement('span');
      //   this.errorIcon.style.marginRight = '1rem';
      //   this.insertBefore(this.errorIcon, this.firstChild);
      //   ReactDOM.render(<Icon icon="EditorWarning" size={24} />, this.errorIcon);
      // }
    }
  }
}

if (!window.customElements.get(ELEMENTS.FieldElement.IDENTIFIER)) {
  window.customElements.define(ELEMENTS.FieldElement.IDENTIFIER, FieldElement);
}
